import { useEffect, useState } from 'react';
import { readCatalogue, type CatalogueEntry } from '../pipedream-catalogue-api';

// Connected provider accounts carry no logo of their own, while the catalogue knows every app's
// mark. Logos the catalogue has shown are remembered here; the rest are read by exact slug through
// the catalogue's recommendation lookup (at most eight per request), once per page load.
const known = new Map<string, string | null>();
const inFlight = new Set<string>();
const listeners = new Set<() => void>();
const SLUG = /^[a-z0-9_]{1,100}$/;
const LOOKUP_LIMIT = 8;

const notify = () => listeners.forEach((listener) => listener());

/** Remember the marks of catalogue entries already on screen. */
export function rememberAppLogos(apps: CatalogueEntry[]) {
  let changed = false;
  for (const app of apps) {
    if (app.provider && app.provider !== 'pipedream') continue;
    if (!app.img_src || known.get(app.slug) === app.img_src) continue;
    known.set(app.slug, app.img_src);
    changed = true;
  }
  if (changed) notify();
}

function lookUp(slugs: string[]) {
  for (const slug of slugs) inFlight.add(slug);
  void readCatalogue('', undefined, slugs.join(','))
    .then((response) => {
      for (const slug of slugs) if (!known.has(slug)) known.set(slug, null);
      rememberAppLogos(response.apps);
      notify();
    })
    .catch(() => {
      for (const slug of slugs) if (!known.has(slug)) known.set(slug, null);
      // The marks fall back to their letter now, not whenever something else re-renders.
      notify();
    })
    .finally(() => {
      for (const slug of slugs) inFlight.delete(slug);
    });
}

/**
 * The catalogue mark for a provider app slug: a URL, null when the app has none (or the lookup
 * failed), or undefined while it is still being read.
 */
export function useAppLogos(slugs: (string | null)[]) {
  const [, setVersion] = useState(0);
  useEffect(() => {
    const listener = () => setVersion((value) => value + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  const wanted = [...new Set(slugs.filter((slug): slug is string => Boolean(slug)))]
    .filter((slug) => SLUG.test(slug))
    .sort();
  const key = wanted.join(',');
  useEffect(() => {
    const missing = wanted.filter((slug) => !known.has(slug) && !inFlight.has(slug));
    for (let index = 0; index < missing.length; index += LOOKUP_LIMIT)
      lookUp(missing.slice(index, index + LOOKUP_LIMIT));
    // `wanted` is derived from `key`.
  }, [key]);
  return (slug: string | null): string | null | undefined =>
    !slug || !SLUG.test(slug) ? null : known.has(slug) ? (known.get(slug) ?? null) : undefined;
}
